"use client";

import { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Points, PointMaterial } from "@react-three/drei";
import * as THREE from "three";

/* ─── floating gold dust particles ─── */
function GoldParticles({ count = 1800 }: { count?: number }) {
  const ref = useRef<THREE.Points>(null);

  const [positions, sizes] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 2.5 + Math.random() * 5.5;
      pos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
      sz[i] = 0.005 + Math.random() * 0.025;
    }
    return [pos, sz];
  }, [count]);

  useFrame(({ clock, pointer }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    ref.current.rotation.y = t * 0.045;
    ref.current.rotation.x = Math.sin(t * 0.02) * 0.12;
    // subtle mouse parallax
    ref.current.rotation.z += (pointer.x * 0.04 - ref.current.rotation.z) * 0.04;
  });

  return (
    <Points ref={ref} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#C9A84C"
        size={0.018}
        sizeAttenuation
        depthWrite={false}
        opacity={0.75}
        blending={THREE.AdditiveBlending}
      />
    </Points>
  );
}

/* ─── inner bright core halo ─── */
function CoreHalo() {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    const mat = ref.current.material as THREE.MeshBasicMaterial;
    mat.opacity = 0.04 + Math.sin(t * 0.6) * 0.015;
    ref.current.scale.setScalar(1 + Math.sin(t * 0.4) * 0.05);
  });

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[2.2, 32, 32]} />
      <meshBasicMaterial
        color="#C9A84C"
        transparent
        opacity={0.04}
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

/* ─── orbiting rings ─── */
function Ring({ radius, speed, tilt }: { radius: number; speed: number; tilt: number }) {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.z = clock.getElapsedTime() * speed;
  });

  return (
    <mesh ref={ref} rotation={[tilt, 0, 0]}>
      <torusGeometry args={[radius, 0.004, 8, 120]} />
      <meshBasicMaterial
        color="#C9A84C"
        transparent
        opacity={0.12}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

/* ─── small floating orbs ─── */
function FloatingOrbs() {
  const orbs = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      angle: (i / 12) * Math.PI * 2,
      radius: 1.4 + Math.random() * 2.2,
      speed: 0.15 + Math.random() * 0.2,
      y: (Math.random() - 0.5) * 3.5,
      phase: Math.random() * Math.PI * 2,
      size: 0.025 + Math.random() * 0.04,
    })), []);

  const refs = useRef<THREE.Mesh[]>([]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    orbs.forEach((orb, i) => {
      const mesh = refs.current[i];
      if (!mesh) return;
      const angle = orb.angle + t * orb.speed;
      mesh.position.x = Math.cos(angle) * orb.radius;
      mesh.position.z = Math.sin(angle) * orb.radius;
      mesh.position.y = orb.y + Math.sin(t * 0.5 + orb.phase) * 0.3;
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.35 + Math.sin(t * 0.8 + orb.phase) * 0.2;
    });
  });

  return (
    <>
      {orbs.map((orb, i) => (
        <mesh
          key={i}
          ref={(el) => { if (el) refs.current[i] = el; }}
        >
          <sphereGeometry args={[orb.size, 8, 8]} />
          <meshBasicMaterial
            color="#E8D5A3"
            transparent
            opacity={0.4}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
    </>
  );
}

/* ─── camera drift ─── */
function CameraDrift() {
  const { camera } = useThree();

  useFrame(({ clock, pointer }) => {
    const t = clock.getElapsedTime();
    camera.position.x += (pointer.x * 0.6 - camera.position.x) * 0.025;
    camera.position.y += (-pointer.y * 0.4 - camera.position.y) * 0.025;
    camera.position.z = 7.5 + Math.sin(t * 0.18) * 0.3;
    camera.lookAt(0, 0, 0);
  });

  return null;
}

/* ─── exported scene ─── */
export default function HeroThreeScene() {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <Canvas
        camera={{ position: [0, 0, 7.5], fov: 65 }}
        gl={{ antialias: true, alpha: true }}
        style={{ background: "transparent" }}
        dpr={[1, 1.5]}
      >
        <Suspense fallback={null}>
          <CameraDrift />
          <GoldParticles count={1600} />
          <CoreHalo />
          <Ring radius={3.2} speed={0.08} tilt={Math.PI / 5} />
          <Ring radius={4.1} speed={-0.055} tilt={Math.PI / 3.2} />
          <Ring radius={5.0} speed={0.035} tilt={Math.PI / 7} />
          <FloatingOrbs />
          <ambientLight intensity={0.3} color="#C9A84C" />
        </Suspense>
      </Canvas>

      {/* radial glow */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 55% 45% at 50% 48%, rgba(201,168,76,0.055) 0%, transparent 68%)",
        pointerEvents: "none",
      }} />
      {/* vignette */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(to right, rgba(10,9,8,0.65) 0%, transparent 20%, transparent 80%, rgba(10,9,8,0.65) 100%)",
        pointerEvents: "none",
      }} />
      {/* bottom fade */}
      <div className="absolute inset-x-0 bottom-0" style={{
        height: "42%",
        background: "linear-gradient(to bottom, transparent, #0A0908)",
        pointerEvents: "none",
      }} />
    </div>
  );
}
